import React from 'react';
import { useLocation } from 'react-router-dom';
import { Users, TrendingUp, ClipboardList, ShieldCheck, MessageSquare, BarChart3 } from 'lucide-react';
import MarketingNav from '../components/layout/MarketingNav';
import MarketingFooter from '../components/layout/MarketingFooter';
import Section from '../components/common/Section';
import Button from '../components/ui/Button';

const ForCliniciansPage = () => {
  const { search } = useLocation();
  const audience = new URLSearchParams(search).get('audience') || 'clinician';

  const heroByAudience = {
    clinician: '/Therapist desk.png',
    clergy: '/Window Moment.png',
    coach: '/couch.png',
    enterprise: '/Walk in Park.png',
  };

  const heroImage = heroByAudience[audience] || heroByAudience.clinician;

  const handleDownloadClinicianBrief = () => {
    const link = document.createElement('a');
    link.href = '/MHDF_Momentum_Project_Clinician_Brief.pdf';
    link.download = 'MHDF_Momentum_Clinician_Brief.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen site-canvas">
      <MarketingNav transparent={true} />

      <main>
        {/* Hero Section */}
        <section
          className="marketing-photo-hero"
          style={{ backgroundImage: `url('${heroImage}')` }}
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10" style={{ maxWidth: '1280px', paddingTop: '130px', paddingBottom: '110px' }}>
            <div className="marketing-photo-hero-panel">
              <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6">
                Prescribe structure for the 10,030 minutes between sessions.
              </h1>
              <p className="text-lg text-white/85 mb-8 max-w-2xl leading-relaxed">
                MHDF Momentum provides Clinical pathway integration: daily homework your clients actually do,
                with engagement data you can review. Three core practices (Mind, Move, Journal) plus optional faith support.
              </p>
            </div>
          </div>
        </section>

        {/* Key Benefits */}
        <Section background="white" padding="lg">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6 mb-10">
              {[
                { icon: Users, title: 'Daily Structure', desc: 'Clients stay anchored with consistent, clinician-aligned practices between appointments.' },
                { icon: TrendingUp, title: 'Visible Progress', desc: 'Track engagement streaks, mood trends, and clinical signals in real time.' },
                { icon: ClipboardList, title: 'Treatment Alignment', desc: 'Daily exercises reinforce CBT, motivational interviewing, and habit science frameworks.' },
                { icon: ShieldCheck, title: 'HIPAA Secure', desc: 'Encrypted journaling and full audit logging built in from day one.' },
                { icon: MessageSquare, title: 'Your Insights', desc: 'Review what your clients learned each week to inform your next session.' },
                { icon: BarChart3, title: 'Outcome Data', desc: 'Measure impact with completion rates, mood improvements, and sustained engagement.' },
              ].map((benefit, idx) => {
                const Icon = benefit.icon;
                return (
                  <div
                    key={idx}
                    className="rounded-3xl p-6 text-center transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                    style={{ background: 'linear-gradient(135deg, rgba(15, 42, 63, 0.04) 0%, rgba(15, 42, 63, 0.01) 100%)' }}
                  >
                    <Icon size={40} className="mx-auto mb-4 text-[#2A8C8E]" />
                    <h3 className="text-lg font-extrabold text-neutral-900 mb-2">{benefit.title}</h3>
                    <p className="text-neutral-700 text-sm">{benefit.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </Section>

        {/* Why Clinicians Choose Momentum */}
        <Section background="white" padding="lg">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-extrabold text-neutral-900 text-center mb-10">Why clinicians trust Momentum</h2>
            <div className="space-y-4">
              {[
                'Clients stay engaged and accountable on the days you don\'t see them.',
                'You get meaningful data about what\'s actually happening between sessions.',
                'Daily practices are grounded in evidence-based treatment frameworks.',
                'Your clinical workflow is respected—Momentum fits into your practice, not the other way around.',
                'HIPAA compliance and encryption mean you can confidently recommend it to clients.'
              ].map((point, i) => (
                <div key={i} className="flex items-start gap-4 p-4 rounded-2xl" style={{ background: 'rgba(255, 209, 102, 0.08)' }}>
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ background: 'linear-gradient(180deg, #2dd4a3 0%, #1ab88b 100%)' }}>
                    <span className="text-white text-sm font-bold">✓</span>
                  </div>
                  <p className="text-lg text-neutral-800">{point}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Resources */}
        <Section background="white" padding="lg">
          <div className="max-w-4xl mx-auto text-center mb-8">
            <h2 className="text-2xl font-extrabold text-neutral-900 mb-2">Get started with confidence</h2>
            <p className="text-neutral-700">Review our clinician documentation and see how Momentum integrates with your practice.</p>
          </div>
          
          <div className="max-w-3xl mx-auto grid md:grid-cols-2 gap-6">
            <button
              onClick={handleDownloadClinicianBrief}
              className="rounded-3xl p-6 text-center transition-all duration-300 hover:shadow-lg hover:-translate-y-1 border-2"
              style={{ borderColor: '#D9E5E7', background: 'white' }}
            >
              <ClipboardList size={40} className="mx-auto mb-4 text-[#2A8C8E]" />
              <h3 className="text-lg font-extrabold text-neutral-900 mb-2">Clinician Brief</h3>
              <p className="text-neutral-700 text-sm">Download PDF with clinical overview, outcomes framework, and integration tips.</p>
            </button>

            <button
              onClick={() => window.location.href = '/preview/clinician'}
              className="rounded-3xl p-6 text-center transition-all duration-300 hover:shadow-lg hover:-translate-y-1 border-2"
              style={{ borderColor: '#D9E5E7', background: 'white' }}
            >
              <Users size={40} className="mx-auto mb-4 text-[#2A8C8E]" />
              <h3 className="text-lg font-extrabold text-neutral-900 mb-2">Clinician Dashboard</h3>
              <p className="text-neutral-700 text-sm">Experience the admin dashboard firsthand and see how you'll manage clients.</p>
            </button>
          </div>
        </Section>

        {/* Final CTA Section */}
        <Section background="white" padding="lg">
          <div className="hero-marble-bg rounded-3xl p-8 sm:p-10 text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
              Ready to support your clients better?
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Join compassionate clinicians who are amplifying their impact with Momentum's structured between-session support.
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 flex-wrap">
              <Button 
                size="lg" 
                className="!py-4 !rounded-2xl !px-8 text-white font-semibold"
                style={{
                  background: '#ffd166',
                  boxShadow: '0 8px 16px rgba(255, 209, 102, 0.35)',
                  color: '#041b12'
                }}
                onClick={() => window.location.href = '/register?role=clinician'}
              >
                Register as Clinician
              </Button>
              <Button 
                size="lg" 
                className="!py-4 !rounded-2xl !px-8 font-semibold"
                style={{
                  background: 'transparent',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => window.location.href = '/how-it-works'}
              >
                See How It Works
              </Button>
            </div>
          </div>
        </Section>
      </main>

      <MarketingFooter />
    </div>
  );
};

export default ForCliniciansPage;
